import {
  Box,
  Container,
  Grid2 as Grid,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { disconnectWebsocket } from "../../../store/actions/websocketActions";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Advertising } from "../../admin/management/Advertising";
import { Registration } from "../../admin/management/Registration";
import { LeavingReasons } from "../../admin/management/LeavingReasons";
import { Staff } from "../../admin/management/Staff";
import { FunFacts } from "../../admin/management/FunFacts";
import { NewDiscounts } from "../../admin/management/NewDiscounts";
import { DemoContractors } from "../../admin/management/DemoContractors";
import { PermitTypeLicenseMapping } from "../../admin/management/PermitTypeLicenseMapping";
import { RemoteConnections } from "../../admin/management/RemoteConnections";
import { SubAdminDomains } from "../../admin/management/SubAdminDomains";
import { FormDigUsers } from "../../admin/management/FormDigUsers";
import { PackageCompletionNotificationRecipients } from "../../admin/management/PackageCompletionNotificationRecipients";
import { shouldShowFormDigitizationUsers } from "../../../utils/domainUtils";

const buildSections = (managementData, setManagementData, isSm) => [
  {
    key: "funFacts",
    title: "Fun Facts",
    render: () => (
      <FunFacts
        title="Fun Facts"
        funFactsRows={managementData?.funFacts}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
  {
    key: "advertising",
    title: "Advertising",
    render: () => (
      <Advertising
        title="Advertising"
        advertisingRows={managementData?.advertising}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
  {
    key: "registration",
    title: "Registration",
    render: () => (
      <Registration
        title="Registration"
        registrationRows={managementData?.additional_registration}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
  {
    key: "newDiscounts",
    title: "New Signup Users Discount",
    render: () => (
      <NewDiscounts
        title="New Signup Users Discount"
        discountsRows={managementData?.new_reg_discount}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
  {
    key: "leavingReasons",
    title: "Leaving Reasons",
    render: () => (
      <LeavingReasons
        title="Leaving Reasons"
        leavingRows={managementData?.leaving_reasons}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
  {
    key: "staff",
    title: "Admin Registrations",
    render: () => (
      <Staff
        title="Admin Registrations"
        staffRows={managementData?.admin_registration}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
  ...(shouldShowFormDigitizationUsers()
    ? [
        {
          key: "formDigUsers",
          title: "Form Digitization Users",
          render: () => <FormDigUsers />,
        },
      ]
    : []),
  {
    key: "permitMapping",
    title: "Permit Type License Mapping",
    render: () => (
      <PermitTypeLicenseMapping
        title="Permit Type License Mapping"
        permitMapping={managementData?.permit_mapping}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
  {
    key: "remoteConnections",
    title: "Remote Connections",
    render: () => (
      <RemoteConnections
        title="Remote Connections"
        remoteConnectionsRows={managementData?.remote_connections}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
  {
    key: "packageCompletionNotificationRecipients",
    title: "Package Completion Notification Recipients",
    render: () => (
      <PackageCompletionNotificationRecipients
        recipients={managementData?.package_completion_notification_recipients}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
  {
    key: "subAdminDomains",
    title: "Sub Admin Domains",
    render: () => (
      <SubAdminDomains
        title="Sub Admin Domains"
        subAdminDomainsRows={managementData?.sub_admin_emails}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
  {
    key: "demoContractors",
    title: "Demo Contractors",
    render: () => (
      <DemoContractors
        title="Demo Contractors"
        demoLicensesRow={managementData?.demo_contractors}
        setManagementData={setManagementData}
        isSm={isSm}
      />
    ),
  },
];

const Management = () => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const [managementData, setManagementData] = useState();
  const [dataLoading, setDataLoading] = useState(true);
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [expanded, setExpanded] = useState({});

  const getDataRef = useRef();

  const dispatchCount = useRef(0);

  useEffect(() => {
    if (currentUser?.user) {
      if (currentUser?.user?.role !== "admin" || !currentUser?.user?.admin) {
        if (dispatchCount.current < 1) {
          dispatch(disconnectWebsocket(currentSession));
          navigate("/login");
          dispatchCount.current += 1;
        }
      }
    }
  }, [currentUser?.user, dispatch, currentSession, navigate]);

  useEffect(() => {
    const getData = async () => {
      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/data/managementData`,
          { currentSession: currentSession },
          { withCredentials: true },
        );

        setManagementData(resp.data.data);

        setDataLoading(false);
      } catch (e) {
        setDataLoading(false);
      }
    };

    if (
      !currentUser ||
      currentUser?.user?.role !== "admin" ||
      !currentUser?.user?.admin
    ) {
      return;
    } else {
      if (!getDataRef?.current) {
        getDataRef.current = true;
        getData();
      }
    }
  }, [currentSession, currentUser]);

  const handleAccordionChange = (key) => (_event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [key]: isExpanded }));
  };

  const sections = buildSections(managementData, setManagementData, isSm);

  return (
    <>
      {dataLoading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <CircularProgress color="warning" size="10rem" />
          </Box>
        </>
      ) : (
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 3,
            backgroundColor: "#EEF2F6",
          }}
        >
          <Container maxWidth="xl" sx={{ mb: 4 }}>
            <Grid container spacing={2}>
              {sections.map(({ key, title, render }) => (
                <Grid key={key} size={{ xs: 12, md: 12, lg: 12 }}>
                  <Accordion
                    expanded={!!expanded[key]}
                    onChange={handleAccordionChange(key)}
                    disableGutters
                    TransitionProps={{ unmountOnExit: true }}
                    sx={{
                      "&:before": { display: "none" },
                      borderRadius: "20px !important",
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`${key}-content`}
                      id={`${key}-header`}
                      sx={{ padding: "0px 24px" }}
                      slotProps={{
                        content: {
                          sx: {
                            margin: "24px 0px 22px",
                          },
                        },
                      }}
                    >
                      <Typography variant="h6" component="span">
                        {title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: "8px 24px 16px" }}>
                      {render()}
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}
    </>
  );
};

export default Management;
